import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDeleteConfirm from "hooks/useDeleteConfirm";
import { HAS_PERMISSION } from "services/user";
import { AI_PROMPT_HANDLE, AI_MESSAGES_LIST } from "permissions/ai";
import {
  useChatMessages,
  useChatThreads,
  sendChatPrompt,
  deleteChatThread,
  fetchOlderMessages,
  INSUFFICIENT_BALANCE_ERROR,
} from "../api";
import {
  CHAT_ALLOWED_FILE_EXTENSIONS,
  CHAT_MAX_FILE_SIZE_BYTES,
  CHAT_MAX_PROMPT_LENGTH,
  getThreadUuid,
} from "../constants";
import { groupThreadsByDate } from "../utils/groupThreadsByDate";

export function useChatSession({
  initialHistoryOpen = false,
  historyMode = "overlay",
} = {}) {
  const { t } = useTranslation();
  const { confirmDelete } = useDeleteConfirm();

  const [activeThreadId, setActiveThreadId] = useState(
    localStorage.getItem("activeThreadId") || ""
  );
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nextPageCursor, setNextPageCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  const [isHistoryOpen, setIsHistoryOpen] = useState(initialHistoryOpen);
  const [threadsPage, setThreadsPage] = useState(1);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [allThreads, setAllThreads] = useState([]);
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const threadsContainerRef = useRef(null);
  const skipAutoScrollRef = useRef(false);

  const hasViewPermission = HAS_PERMISSION(AI_MESSAGES_LIST);
  const hasPromptPermission = HAS_PERMISSION(AI_PROMPT_HANDLE);

  const { data, fetchLoading, isFetching, isSuccess } = useChatMessages(
    hasViewPermission && activeThreadId && activeThreadId !== "null"
      ? activeThreadId
      : null
  );

  const {
    data: threadsData,
    currentPage: threadsCurrentPage,
    lastPage: threadsLastPage,
    isFetching: threadsFetching,
    isPlaceholderData: threadsIsPlaceholder,
  } = useChatThreads(
    hasViewPermission && isHistoryOpen ? threadRefreshKey : null,
    threadsPage
  );

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior,
      });
    }
  }, []);

  useEffect(() => {
    if (!activeThreadId || activeThreadId === "null") {
      setMessages([]);
      setNextPageCursor(null);
      setHasMoreMessages(false);
      return;
    }

    if (isSuccess && !isFetching && data) {
      setMessages(data.messages || []);
      setNextPageCursor(data.next_page_cursor || null);
      setHasMoreMessages(data.has_more || false);
    }
  }, [data, activeThreadId, isSuccess, isFetching]);

  useEffect(() => {
    if (!threadsData || threadsIsPlaceholder) return;

    if (threadsPage === 1) {
      setAllThreads(threadsData);
    } else {
      setAllThreads((prev) => {
        const threadKey = (thread) => thread.id ?? getThreadUuid(thread);
        const existingIds = new Set(prev.map(threadKey));
        const newThreads = threadsData.filter(
          (thread) => !existingIds.has(threadKey(thread))
        );
        return newThreads.length > 0 ? [...prev, ...newThreads] : prev;
      });
    }
    setHasMoreThreads(threadsCurrentPage < threadsLastPage);
  }, [
    threadsData,
    threadsPage,
    threadsCurrentPage,
    threadsLastPage,
    threadsIsPlaceholder,
  ]);

  useEffect(() => {
    if (messages.length > 0 && !loadingMore && !skipAutoScrollRef.current) {
      scrollToBottom("smooth");
    }
  }, [messages, loading, loadingMore, scrollToBottom]);

  useEffect(() => {
    if (activeThreadId) {
      localStorage.setItem("activeThreadId", activeThreadId);
    } else {
      localStorage.removeItem("activeThreadId");
    }
  }, [activeThreadId]);

  useEffect(() => {
    if (!threadsFetching) {
      setThreadsLoading(false);
    }
  }, [threadsFetching]);

  const handleScroll = async (e) => {
    const container = e.target;
    if (
      !container ||
      loadingMore ||
      !hasMoreMessages ||
      !nextPageCursor ||
      !activeThreadId
    ) {
      return;
    }

    if (container.scrollTop < 20) {
      setLoadingMore(true);
      skipAutoScrollRef.current = true;
      const prevScrollHeight = container.scrollHeight;

      try {
        const response = await fetchOlderMessages(
          activeThreadId,
          nextPageCursor
        );
        if (response?.messages?.length) {
          setMessages((prev) => [...response.messages, ...prev]);
          setNextPageCursor(response.next_page_cursor || null);
          setHasMoreMessages(response.has_more || false);

          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
            skipAutoScrollRef.current = false;
          }, 30);
        } else {
          setNextPageCursor(null);
          setHasMoreMessages(false);
          skipAutoScrollRef.current = false;
        }
      } catch (err) {
        console.error("Error loading older messages:", err);
        skipAutoScrollRef.current = false;
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const handleThreadsScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const bottomThreshold = scrollHeight - clientHeight - 30;

      if (scrollTop >= bottomThreshold && hasMoreThreads && !threadsLoading) {
        setThreadsLoading(true);
        setThreadsPage((prev) => prev + 1);
      }
    },
    [hasMoreThreads, threadsLoading]
  );

  const handleNewChat = useCallback(() => {
    setActiveThreadId("");
    setMessages([]);
    setNextPageCursor(null);
    setHasMoreMessages(false);
    if (historyMode === "overlay") {
      setIsHistoryOpen(false);
    }
  }, [historyMode]);

  const handleSendMessage = async (textOverride) => {
    const textToSend = textOverride !== undefined ? textOverride : inputValue;
    if (!textToSend.trim() && !selectedFile) return;
    if (loading) return;

    if (!hasPromptPermission) {
      message.error(t("chat.no_permission"));
      return;
    }

    if (textToSend.length > CHAT_MAX_PROMPT_LENGTH) {
      message.error(t("chat.prompt_max_length"));
      return;
    }

    setLoading(true);
    const tempMessageId = `temp-${Date.now()}`;
    const userMessage = {
      id: tempMessageId,
      role: "user",
      content: textToSend,
      files: selectedFile ? [{ filename: selectedFile.name }] : [],
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setSelectedFile(null);

    const formData = new FormData();
    formData.append("prompt", textToSend);
    if (activeThreadId && activeThreadId !== "null") {
      formData.append("thread_id", activeThreadId);
    }
    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }

    try {
      scrollToBottom("smooth");
      const response = await sendChatPrompt(formData);
      if (Array.isArray(response.messages)) {
        setMessages(response.messages);
        setNextPageCursor(response.next_page_cursor || null);
        setHasMoreMessages(response.has_more || false);
        setActiveThreadId(response.thread_id);
        setThreadsPage(1);
        setThreadRefreshKey((prev) => prev + 1);
      }
    } catch (err) {
      // Insufficient wallet balance: drop the optimistic user bubble and show a
      // clear inline chat message instead of the generic send-failure toast.
      if (err?.code === INSUFFICIENT_BALANCE_ERROR) {
        setMessages((prev) => [
          ...prev.filter((msg) => msg.id !== tempMessageId),
          {
            id: `insufficient-balance-${Date.now()}`,
            role: "assistant",
            isError: true,
            content: t("chat.insufficient_balance"),
          },
        ]);
      } else {
        console.error("Error sending chat prompt:", err);
        message.error(t("chat.error_send"));
        setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestionText) => {
    setInputValue(suggestionText);
    handleSendMessage(suggestionText);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExt = file.name.split(".").pop().toLowerCase();

    if (!CHAT_ALLOWED_FILE_EXTENSIONS.includes(fileExt)) {
      message.error(t("chat.file_type_not_supported"));
      return;
    }
    if (file.size > CHAT_MAX_FILE_SIZE_BYTES) {
      message.error(t("chat.file_too_large"));
      return;
    }
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleCopyThread = (e, threadId) => {
    e.stopPropagation();
    navigator.clipboard.writeText(threadId);
    message.success(t("chat.thread_id_copied"));
  };

  const handleDeleteThread = (e, id, threadIdOfDeleted) => {
    e.stopPropagation();
    confirmDelete(async () => {
      try {
        const response = await deleteChatThread(id);
        if (response.status) {
          message.success(t("chat.delete_success"));
          setThreadsPage(1);
          setThreadRefreshKey((prev) => prev + 1);
          if (activeThreadId === threadIdOfDeleted) {
            handleNewChat();
          }
        }
      } catch (err) {
        message.error(t("chat.error_load"));
      }
    }, "chat.delete_confirm");
  };

  const handleSelectThread = (threadUuid) => {
    skipAutoScrollRef.current = false;
    setMessages([]);
    setNextPageCursor(null);
    setActiveThreadId(threadUuid);
    if (historyMode === "overlay") {
      setIsHistoryOpen(false);
    }
  };

  const toggleHistory = () => {
    setThreadsPage(1);
    if (!isHistoryOpen) setAllThreads([]);
    setIsHistoryOpen((prev) => !prev);
  };

  const groupedThreads = groupThreadsByDate(allThreads, searchQuery, t);

  return {
    t,
    hasViewPermission,
    hasPromptPermission,
    activeThreadId,
    messages,
    inputValue,
    setInputValue,
    selectedFile,
    loading,
    loadingMore,
    fetchLoading,
    isFetching,
    isHistoryOpen,
    setIsHistoryOpen,
    searchQuery,
    setSearchQuery,
    groupedThreads,
    threadsFetching,
    threadsLoading,
    threadsPage,
    fileInputRef,
    messagesContainerRef,
    threadsContainerRef,
    handleScroll,
    handleThreadsScroll,
    handleNewChat,
    handleSendMessage,
    handleSuggestionClick,
    handleKeyPress,
    handleFileSelect,
    handleFileChange,
    handleRemoveFile,
    handleCopyThread,
    handleDeleteThread,
    handleSelectThread,
    toggleHistory,
    getThreadUuid,
  };
}
